import type { StyleOptions } from '@aus-platform/cesium';
import { ClampStatus, MVTImageryProvider } from '@aus-platform/cesium';
import {
    Icon,
    IconIdentifier,
    Pill,
    PillShape,
    PillVariant,
    Placement,
    Tooltip,
    toast,
} from '@aus-platform/design-system';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import type { Cartesian2 } from 'cesium';
import { ImageryLayer, SceneMode } from 'cesium';
import { filter, isEmpty, isNil, size } from 'lodash';
import type {
    WorkspaceLayer,
    WorkspaceLayerListObj,
} from 'map-3d/components/map-3d-sidebar/shared';
import {
    WorkspaceRightSideCard,
    addTerrainLayer,
    addWorkspaceLayer,
    clearWorkspaceSlug,
    selectMap3DState,
    selectMap3dDataset,
    selectMap3dWorkspace,
    setIsWorkspaceLoading,
    setRightSideCard,
    updateWorkspaceLayer,
} from 'map-3d/shared/map-3d-slices';
import type { Map3DTerrainProviderType } from 'map-3d/types';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Spinner } from 'react-bootstrap';
import {
    AreaCategory,
    type Feature,
    FeatureType,
    type FeaturesStyles,
    FileType,
    LayerType,
    type UpdateLayer,
    handleResponseErrorMessage,
    handleResponseMessage,
    useCogMetadata,
    useFeatureInfo,
    useFeatureList,
    useLayer,
    useUpdateLayer,
} from 'shared/api';
import { addCanvasClickListener } from 'shared/cesium';
import { getInitialRescaleValue } from 'shared/components';
import { ListItem, ListItemVariant } from '../../../../shared/components';
import {
    DatasetDetailsTooltip,
    FileStatusIndicatorMapping,
    LayerIcon,
    getGeometryType,
    getLayerMetadata,
    layerHasTextboxFeatures,
    loadSingleFeature,
    removeMapLayer,
    zoomToLayer,
} from '../../shared';
import {
    DxfSublayerList,
    FeatureCountTree,
    SelectAreaCategoryModal,
    areaCategoryOptions,
    areaCategoryPillLabel,
} from './components';
import { LayerActionItem } from './components/layer-action-item';
import { LayerMeatBallMenu } from './components/layer-meatball-menu';
import {
    canLayerShowHistogram,
    isLayerPresentOnMap,
    isLayerProcessing,
    resolveActiveLayerFeatureId,
} from './helpers';
import type { WorkSpaceActiveLayerPropType } from './types';

export const WorkSpaceActiveLayer: React.FC<WorkSpaceActiveLayerPropType> = ({
  layer,
  className,
  isChecked,
  onClick,
  index,
}) => {
  // Hooks.
  const dispatch = useAppDispatch();

  // Selectors.
  const { cesiumProxy, isWorkspaceLoading, currentSceneMode } =
    useAppSelector(selectMap3DState);
  const {
    actionTools,
    drawingTools,
    terrainProvider,
    currentEditableLayerId,
    workspaceLayers,
    workspaceSlug,
  } = useAppSelector(selectMap3dWorkspace);
  const { activeWorkspaceSite } = useAppSelector(selectMap3dDataset);

  // States.
  const [showFeatureCount, setShowFeatureCount] = useState<boolean>(false);
  const [showSelectAreaCategoryModal, setShowSelectAreaCategoryModal] =
    useState<boolean>(false);
  const [clickedFeatureId, setClickedFeatureId] = useState<string | null>(null);
  const [isPickingFeature, setIsPickingFeature] = useState<boolean>(false);
  const [loadedFeatures, setLoadedFeatures] = useState<Feature[]>([]);
  const [showSublayers, setShowSublayers] = useState<boolean>(true);

  const cadNodes = layer?.nodes ?? [];
  const [hiddenCadNodeIds, setHiddenCadNodeIds] = useState<Set<string>>(
    () => new Set(),
  );

  // useMemos.
  const allNodeIds = useMemo(() => cadNodes.map((node) => node.id), [cadNodes]);
  const cvTilesTool = useMemo(
    () => cesiumProxy?.cvTilesTools.cvTilesTool,
    [cesiumProxy],
  );

  // Refs.
  const removeClickListenerRef = useRef<(() => void) | null>(null);
  const workspaceLayersRef = useRef(workspaceLayers);
  workspaceLayersRef.current = workspaceLayers;
  const clickedFeatureIdRef = useRef(clickedFeatureId);
  clickedFeatureIdRef.current = clickedFeatureId;
  const activeImageryLayerRef = useRef<ImageryLayer | undefined>();
  activeImageryLayerRef.current =
    layer.mapLayer instanceof ImageryLayer ? layer.mapLayer : undefined;

  // Constants.
  const isSelectingFeature = isPickingFeature || !isNil(clickedFeatureId);
  const isInEditMode = layer.id === currentEditableLayerId;
  const isIn2DView = currentSceneMode === SceneMode.SCENE2D;
  const isPointCloudIn2D = layer.type === LayerType.PointCloud && isIn2DView;
  const isCadLayer = layer.type === LayerType.Cad;
  const clampToSurface = layer.cvTilesData?.clampToSurface ?? false;
  const isClampEnabled = isIn2DView || clampToSurface;

  const shouldFetchCogMetadata =
    layer.type === LayerType.CapturedDsm &&
    !isLayerProcessing(layer) &&
    !isLayerPresentOnMap(layer) &&
    !isNil(layer.iteration?.id);

  const shouldFetchLayerData =
    !isLayerPresentOnMap(layer) &&
    !isLayerProcessing(layer) &&
    layer.type !== LayerType.CapturedDsm;

  const showAreaCategoryPill =
    layer.areaCategory !== AreaCategory.Other &&
    layer.type === LayerType.Vector;

  // Apis.
  // TODO: Remove after implementing DSM as a layer.
  const {
    data: cogMetadata,
    isSuccess: isCOGMetadataSuccess,
    isLoading: isCOGMetadataLoading,
  } = useCogMetadata(
    {
      iterationId: layer.iteration?.id ?? '',
      fileType: FileType.CapturedDsm,
    },
    shouldFetchCogMetadata,
  );

  const {
    mutate: sendLayerUpdateRequest,
    data: updateLayerResponse,
    isError: isErrorUpdateLayer,
    error: updateLayerError,
    isPending: isLoadingUpdateLayer,
    isSuccess: isSuccessUpdateLayer,
  } = useUpdateLayer();

  const {
    data: layerResponse,
    isFetching: isLoadingLayerResponse,
    isSuccess: isSuccessLayerResponse,
    isError: isErrorLayerResponse,
    error: layerResponseError,
    refetch: refetchLayer,
  } = useLayer(layer.id, shouldFetchLayerData);

  const {
    data: featuresResponse,
    isFetching: isLoadingFeaturesResponse,
    isSuccess: isSuccessFeaturesResponse,
    isError: isErrorFeaturesResponse,
    error: featuresResponseError,
    refetch: refetchFeatures,
  } = useFeatureList(layer.id, undefined, false);

  // Fetch individual feature when clicked in edit mode.
  const {
    data: clickedFeatureResponse,
    isSuccess: isSuccessClickedFeature,
    isError: isErrorClickedFeature,
    error: clickedFeatureError,
  } = useFeatureInfo(
    clickedFeatureId ?? '',
    !isNil(clickedFeatureId) && isInEditMode,
  );

  // Conditional variables.
  const isLoading =
    isLayerProcessing(layer) ||
    (isCOGMetadataLoading && canLayerShowHistogram(layer)) ||
    isLoadingLayerResponse ||
    isLoadingFeaturesResponse;

  // Handlers.
  const getListVariant = (): ListItemVariant => {
    if (layer.id === currentEditableLayerId) {
      return ListItemVariant.Warning;
    } else {
      return ListItemVariant.Primary;
    }
  };

  const getStyleOptions = (featuresStyles?: FeaturesStyles): StyleOptions => ({
    pointStyleOptions: featuresStyles?.point,
    lineStyleOptions: featuresStyles?.line,
    polygonStyleOptions: featuresStyles?.polygon,
    textboxStyleOptions: featuresStyles?.textbox,
  });

  // Redux state handlers.
  const updateLayer = (layers: WorkspaceLayerListObj) => {
    dispatch(updateWorkspaceLayer(layers));
    dispatch(clearWorkspaceSlug());
  };

  const clearCadLayerOnboarding = useCallback(() => {
    const currentLayer = workspaceLayersRef.current[layer.id];

    if (!currentLayer?.showCadLayerOnboarding) {
      return;
    }

    dispatch(
      updateWorkspaceLayer({
        [layer.id]: {
          ...currentLayer,
          showCadLayerOnboarding: false,
        },
      }),
    );
  }, [dispatch, layer.id]);

  /**
   * Shows all features hidden during editing in an MVT layer.
   * This is called when exiting edit mode to restore all features that were hidden during editing to visibility.
   *
   * @param mapLayer - The imagery layer to show all features for
   */
  const showAllHiddenFeatures = async (
    mapLayer: ImageryLayer | undefined,
  ): Promise<void> => {
    if (!(mapLayer instanceof ImageryLayer)) {
      return;
    }

    const imageryProvider = mapLayer.imageryProvider;

    if (!(imageryProvider instanceof MVTImageryProvider)) {
      return;
    }

    if (
      typeof (imageryProvider as MVTImageryProvider).showAllFeatures ===
      'function'
    ) {
      try {
        await imageryProvider.showAllFeatures();
      } catch (error) {
        console.error('Error showing all hidden features:', error);
      }
    }
  };

  const addTerrainLayerHandler = (
    terrainLayerPayload: Map3DTerrainProviderType,
  ) => {
    dispatch(addTerrainLayer(terrainLayerPayload));
  };

  // TODO - remove add histogram function, add map layer already handles addition of histogram.
  const addHistogram = (initialRescaleValue, sourceFilePath, metadata) => {
    if (!cesiumProxy) {
      return;
    }

    metadata = metadata ?? layer.histogramData?.metadata;
    sourceFilePath = sourceFilePath ?? layer.histogramData?.sourceFilePath;

    if (!sourceFilePath) {
      return;
    }

    // Remove previous map layer if it exists.
    if (layer.mapLayer) {
      removeMapLayer(cesiumProxy, layer.mapLayer);
    }

    // Add new map layer.
    const mapLayer = cesiumProxy.layerManager.addHistogramLayerToCesium(
      {
        metadata,
        sourceFilePath,
        rescale: initialRescaleValue,
        opacity: layer.histogramData?.opacity ?? 100,
      },
      {
        show: layer.show ?? true,
        zIndex: layer.cesiumIndex,
      },
    );

    const zIndex = layer.zIndex ?? Object.values(workspaceLayers).length;

    updateLayer({
      [layer.id]: {
        ...layer,
        mapLayer,
        zIndex,
        histogramData: {
          metadata,
          sourceFilePath,
          rescale: initialRescaleValue,
          opacity: 100,
        },
      },
    });

    // Zoom to 1st layer selected.
    if (size(workspaceLayers) === 1 && isNil(workspaceSlug)) {
      zoomToLayer(layer, cesiumProxy, activeWorkspaceSite);
    }
  };

  const enableLayerEditing = () => {
    if (isLoadingFeaturesResponse) {
      return;
    }

    const { mapLayer, featuresStyles } = layer;

    let zIndex;
    if (mapLayer && cesiumProxy) {
      zIndex = layer.zIndex ?? cesiumProxy.layerManager.getZIndex(mapLayer);
      // Keep the MVT layer visible for click detection (only for imagery layers).
      if (mapLayer instanceof ImageryLayer) {
        mapLayer.show = true;
      }
    }

    // Eagerly load TextBox features as Cesium Billboard primitives.
    const loadedTextboxes: Feature[] = [];

    if (
      layerHasTextboxFeatures(layer) &&
      !isNil(featuresResponse) &&
      !isNil(drawingTools)
    ) {
      const styleOptions = getStyleOptions(featuresStyles);

      const textboxFeatures = filter(
        featuresResponse.data.features,
        (feature) => feature.type === FeatureType.Textbox,
      );

      for (const feature of textboxFeatures) {
        const existingTextbox = drawingTools.textboxTool.getTextbox(feature.id);

        if (isNil(existingTextbox)) {
          const loadedTextbox = loadSingleFeature(
            feature,
            layer.id,
            drawingTools,
            styleOptions,
          );
          loadedTextboxes.push(loadedTextbox);
        } else {
          loadedTextboxes.push({ ...feature, feature: existingTextbox });
        }
      }

      if (!isEmpty(loadedTextboxes)) {
        setLoadedFeatures(loadedTextboxes);

        if (mapLayer instanceof ImageryLayer) {
          const imageryProvider = mapLayer.imageryProvider;

          if (imageryProvider instanceof MVTImageryProvider) {
            imageryProvider.hideAllTextboxFeatures();
          }
        }
      }
    }

    const updatedLayer: WorkspaceLayer = {
      ...layer,
      features: loadedTextboxes,
      zIndex,
      mapLayer,
    };

    updateLayer({
      [layer.id]: updatedLayer,
    });
    dispatch(setRightSideCard(WorkspaceRightSideCard.FeatureStyling));
  };

  // Handle feature click in edit mode. `position` is already in canvas
  // coordinates, and only a deliberate click (not a drag) gets this far — see
  // addCanvasClickListener.
  const handleFeatureClick = useCallback(
    async (position: Cartesian2) => {
      if (
        !cesiumProxy?.viewer ||
        !isInEditMode ||
        !actionTools?.selectFeatureTool.isActive ||
        !isNil(clickedFeatureIdRef.current)
      ) {
        return;
      }

      const viewer = cesiumProxy.viewer;

      // Create pick ray.
      const pickRay = viewer.camera.getPickRay(position);

      if (!pickRay) {
        return;
      }

      setIsPickingFeature(true);

      try {
        // Pick features from MVT layer.
        const featuresPromise = viewer.imageryLayers.pickImageryLayerFeatures(
          pickRay,
          viewer.scene,
        );

        if (!featuresPromise) {
          return;
        }

        const features = await featuresPromise;

        if (isEmpty(features)) {
          return;
        }


        const featureId = resolveActiveLayerFeatureId(
          features,
          activeImageryLayerRef.current,
        );

        if (!isNil(featureId)) {
          setClickedFeatureId(featureId);
        }
      } catch (error) {
        console.error('Error picking feature:', error);
      } finally {
        setIsPickingFeature(false);
      }
    },
    [cesiumProxy, isInEditMode, actionTools],
  );

  // Layer response handlers.
  const onLayerResponseSuccess = async () => {
    if (!layerResponse) {
      return;
    }

    // Everything the drawing tools currently hold as a primitive has to stay out of
    // the rebuilt tiles. This covers features drawn in this session too: on save they
    // are promoted into the tools' loaded set and persisted, so the refreshed tiles
    // contain them even though they were never clicked and hidden individually.
    const hiddenFeatureIds = isInEditMode
      ? (drawingTools?.loadedFeatureIds ?? [])
      : [];

    if (layer.mapLayer && cesiumProxy) {
      removeMapLayer(cesiumProxy, layer.mapLayer);
    }

    const updatedLayer: WorkspaceLayer = {
      ...layer,
      ...layerResponse,
      layerStatus: FileStatusIndicatorMapping[layerResponse.status],
      refresh: refetchLayer,
    };
    let showMapLayer;

    if (isNil(layer.mapLayer)) {
      // Initially adding map layer.
      showMapLayer = true;
    } else if (currentEditableLayerId === layer.id && !layer.mapLayer.show) {
      /**
       * Layer is in editing mode, we're updating the style.
       * Tiled Layer should be hidden until and unless we get out of editing mode
       **/
      showMapLayer = false;
    } else if (
      !isNil(currentEditableLayerId) &&
      currentEditableLayerId !== layer.id &&
      !layer.mapLayer.show
    ) {
      /**
       * A layer is in editing mode, and we're switching to edit another layer.
       * Previous editable layer should get back to tiled mode.
       * We enable showMapLayer, in order to display the tiled view of previous editable layer.
       **/
      showMapLayer = true;
    } else {
      showMapLayer = layer.mapLayer.show;
    }

    isWorkspaceLoading
      ? await addMapLayerWithRetry(
          updatedLayer,
          layer.show ?? showMapLayer,
          hiddenFeatureIds,
        )
      : await addMapLayerWithoutRetry(
          updatedLayer,
          showMapLayer,
          hiddenFeatureIds,
        );
  };

  const addMapLayerWithoutRetry = async (
    layer: WorkspaceLayer,
    showMapLayer: boolean,
    hiddenFeatureIds: string[] = [],
  ) => {
    if (layer.type === LayerType.SlopeMap) {
      const metadata = getLayerMetadata(layer);
      const sourceFilePath = layer.files?.[0].s3Key;

      // @FIXME 💩 - hack pro max - use function to load histogram layer
      if (metadata && sourceFilePath) {
        const initialRescaleValue = getInitialRescaleValue(
          metadata,
          sourceFilePath,
        );

        initialRescaleValue &&
          addHistogram(initialRescaleValue, sourceFilePath, metadata);
      }
    } else {
      const mapLayer = await cesiumProxy?.layerManager.addMapLayer({
        layer,
        addTerrainLayerCallback: addTerrainLayerHandler,
        show: showMapLayer,
        terrainProvider,
        hiddenFeatureIds,
        hideTextboxFeatures: isInEditMode,
      });

      const zIndex = layer.zIndex ?? Object.values(workspaceLayers).length;
      const isFirstCadMapLoad = isCadLayer && isNil(layer.mapLayer);

      updateLayer({
        [layer.id]: {
          ...layer,
          mapLayer,
          zIndex,
          show: showMapLayer,
          ...(isFirstCadMapLoad && { showCadLayerOnboarding: true }),
        },
      });
    }

    // Zoom to 1st layer selected.
    if (size(workspaceLayers) === 1 && isNil(workspaceSlug)) {
      zoomToLayer(layer, cesiumProxy, activeWorkspaceSite);
    }
  };

  // TODO: Refactor shareable-link workspace loading to use a deterministic
  // WorkspaceLayerLoader with sequential layer insertion and explicit
  // loaded/failed state tracking. For now, scale retries by cesiumIndex
  // to avoid dropping layers in large workspaces.
  const maxRetries = Math.max(
    10,
    (layer.cesiumIndex ?? size(workspaceLayers)) * 2 + 10,
  );

  const addHistogramLayerWithoutRetry = (metadata, sourceFilePath) => {
    const initialRescaleValue = getInitialRescaleValue(
      metadata,
      sourceFilePath,
    );

    initialRescaleValue &&
      addHistogram(initialRescaleValue, sourceFilePath, cogMetadata);
  };

  // @FIXME 💩 - create proper generalized functions.
  const addHistogramLayerWithRetry = async (metadata, sourceFilePath) => {
    if (!cesiumProxy || isNil(layer.zIndex)) {
      return;
    }

    let retries = 0;
    while (retries < maxRetries) {
      const currentImageryCount =
        cesiumProxy.layerManager.getImageryLayerCount();
      const totalLayerCount = cesiumProxy.layerManager.getLength();

      // All shareable workspace layers have been loaded, reset flag
      if (totalLayerCount === size(workspaceLayers)) {
        dispatch(setIsWorkspaceLoading(false));
      }

      if (currentImageryCount >= (layer.cesiumIndex ?? 1)) {
        addHistogramLayerWithoutRetry(metadata, sourceFilePath);
        break;
      } else {
        // Wait for the specified delay before retrying
        await new Promise((resolve) => setTimeout(resolve, 500));
        retries++;
      }
    }

    if (retries === maxRetries) {
      // Handle the case where the layer couldn't be added after maximum retries
      console.error('Failed to add layer after maximum retries');
    }
  };

  const addMapLayerWithRetry = async (
    layer: WorkspaceLayer,
    showMapLayer: boolean,
    hiddenFeatureIds: string[] = [],
  ) => {
    if (!cesiumProxy || isNil(layer.zIndex)) {
      return;
    }

    // For point clouds and CAD layers, add immediately without waiting for imagery ordering.
    if (layer.type === LayerType.PointCloud || isCadLayer) {
      await addMapLayerWithoutRetry(layer, showMapLayer, hiddenFeatureIds);

      // Check if all workspace layers are loaded after adding point cloud.
      const totalLayerCount = cesiumProxy.layerManager.getLength();
      if (totalLayerCount >= size(workspaceLayers)) {
        dispatch(setIsWorkspaceLoading(false));
      }

      return;
    }

    // For imagery layers, wait for proper ordering.
    let retries = 0;
    while (retries < maxRetries) {
      const currentImageryCount =
        cesiumProxy.layerManager.getImageryLayerCount();
      const totalLayerCount = cesiumProxy.layerManager.getLength();

      // All shareable workspace layers have been loaded, reset flag.
      if (totalLayerCount === size(workspaceLayers)) {
        dispatch(setIsWorkspaceLoading(false));
      }

      if (currentImageryCount >= (layer.cesiumIndex ?? 1)) {
        await addMapLayerWithoutRetry(layer, showMapLayer, hiddenFeatureIds);
        break;
      } else {
        // Wait for the specified delay before retrying
        await new Promise((resolve) => setTimeout(resolve, 500));
        retries++;
      }
    }

    if (retries === maxRetries) {
      // Handle the case where the layer couldn't be added after maximum retries
      console.error('Failed to add layer after maximum retries');
    }
  };

  const onCloseSelectAreaCategory = () => {
    setShowSelectAreaCategoryModal(false);
  };

  const onSaveSelectedAreaCategory = (selectedAreaCategory: AreaCategory) => {
    const updateLayerAreaCategoryPayload: UpdateLayer = {
      id: layer.id,
      data: {
        areaCategory: selectedAreaCategory,
      },
    };
    sendLayerUpdateRequest(updateLayerAreaCategoryPayload);
  };

  // useEffects.
  useEffect(() => {
    handleResponseErrorMessage(isErrorLayerResponse, layerResponseError);
  }, [isErrorLayerResponse, layerResponseError]);

  // useEffect - Add map layer.
  useEffect(() => {
    if (!isSuccessLayerResponse || !layerResponse) {
      return;
    }

    onLayerResponseSuccess();
  }, [layerResponse, isSuccessLayerResponse]);

  // useEffect - Update layer on area category update.
  useEffect(() => {
    if (isSuccessUpdateLayer && updateLayerResponse) {
      const updatedAreaCategoryValue: AreaCategory =
        updateLayerResponse.data.areaCategory;

      const updatedWorkspaceLayer: WorkspaceLayer = {
        ...layer,
        areaCategory: updatedAreaCategoryValue,
      };

      dispatch(addWorkspaceLayer(updatedWorkspaceLayer));
      setShowSelectAreaCategoryModal(false);
    }
    handleResponseMessage(
      isSuccessUpdateLayer,
      isErrorUpdateLayer,
      updateLayerResponse,
      updateLayerError,
    );
  }, [
    isSuccessUpdateLayer,
    updateLayerResponse,
    isErrorUpdateLayer,
    updateLayerError,
  ]);

  const handleCadParentVisibilityToggle = useCallback(
    (nextShow: boolean) => {
      if (isNil(cvTilesTool)) {
        return;
      }

      cvTilesTool.setLayerVisible(layer.id, nextShow);

      if (nextShow) {
        setHiddenCadNodeIds(new Set());
      } else {
        setHiddenCadNodeIds(new Set(allNodeIds));
      }

      dispatch(
        updateWorkspaceLayer({ [layer.id]: { ...layer, show: nextShow } }),
      );
    },
    [cvTilesTool, layer, dispatch],
  );

  const handleToggleNodeVisibility = useCallback(
    (nodeId: string) => {
      if (isNil(cvTilesTool)) {
        return;
      }

      const isParentOff = !layer.show;

      if (isParentOff) {
        cvTilesTool.showOnlyNodeAndLayer(layer.id, nodeId);
        setHiddenCadNodeIds(new Set(allNodeIds.filter((id) => id !== nodeId)));
        dispatch(
          updateWorkspaceLayer({ [layer.id]: { ...layer, show: true } }),
        );
      } else {
        const wasHidden = hiddenCadNodeIds.has(nodeId);
        const nextHidden = new Set(hiddenCadNodeIds);

        if (wasHidden) {
          nextHidden.delete(nodeId);
        } else {
          nextHidden.add(nodeId);
        }

        cvTilesTool.setNodeVisibility(layer.id, nodeId, wasHidden);
        setHiddenCadNodeIds(nextHidden);

        const allSublayersHidden = size(nextHidden) === size(allNodeIds);

        if (allSublayersHidden) {
          cvTilesTool.setLayerVisible(layer.id, false);
          dispatch(
            updateWorkspaceLayer({ [layer.id]: { ...layer, show: false } }),
          );
        }
      }
    },
    [cvTilesTool, layer, hiddenCadNodeIds, dispatch],
  );

  const handleClampToSurfaceChange = useCallback(
    (next: boolean) => {
      if (isNil(cvTilesTool)) {
        return;
      }

      const clampStatus = cvTilesTool.setAllClamped(layer.id, next);

      switch (clampStatus) {
        case ClampStatus.Success:
          dispatch(
            updateWorkspaceLayer({
              [layer.id]: {
                ...layer,
                cvTilesData: { ...layer.cvTilesData, clampToSurface: next },
              },
            }),
          );
          break;
        case ClampStatus.Error:
          toast.error(
            next
              ? 'Failed to clamp layer to surface. Please try again.'
              : 'Failed to unclamp layer. Please try again.',
          );
          break;
      }
    },
    [dispatch, layer],
  );

  useEffect(() => {
    if (!isCadLayer || isNil(cvTilesTool)) {
      return;
    }

    cvTilesTool.setAllClamped(layer.id, isClampEnabled);
  }, [isCadLayer, cvTilesTool, layer.id, currentSceneMode]);

  useEffect(() => {
    if (!layer.showCadLayerOnboarding) {
      return;
    }
    const timer = setTimeout(clearCadLayerOnboarding, 5000);

    return () => clearTimeout(timer);
  }, [layer.showCadLayerOnboarding, clearCadLayerOnboarding]);

  // useEffect - Update histogram after DSM COG metadata or Slope map layer fetch.
  // TODO: Merge this with layerData response useEffect hook after ortho and dsm as layers.
  useEffect(() => {
    if (
      layer.type === LayerType.CapturedDsm &&
      isCOGMetadataSuccess &&
      !isLayerPresentOnMap(layer) &&
      layer.histogramData?.sourceFilePath
    ) {
      const { sourceFilePath } = layer.histogramData;

      isWorkspaceLoading
        ? addHistogramLayerWithRetry(cogMetadata, sourceFilePath)
        : addHistogramLayerWithoutRetry(cogMetadata, sourceFilePath);
    }
  }, [cogMetadata]);

  //  Features response
  useEffect(() => {
    handleResponseErrorMessage(isErrorFeaturesResponse, featuresResponseError);
  }, [isErrorFeaturesResponse, featuresResponseError]);

  useEffect(() => {
    if (!isSuccessFeaturesResponse || !featuresResponse) {
      return;
    }

    if (isInEditMode) {
      enableLayerEditing();
    }
  }, [featuresResponse, isInEditMode]);

  // Defer import/select so the wait cursor can paint and the browser can
  // process input between the network response and sync geometry work.
  useEffect(() => {
    if (!isSuccessClickedFeature || !clickedFeatureResponse || !drawingTools) {
      return;
    }

    const featureData = clickedFeatureResponse.data;

    if (featureData.layer !== layer.id) {
      setClickedFeatureId(null);
      return;
    }

    const existingFeature = loadedFeatures.find(
      (feature) => feature.id === featureData.id,
    );
    if (existingFeature && existingFeature.feature) {
      setClickedFeatureId(null);
      return;
    }

    const { featuresStyles, mapLayer } = layer;
    const styleOptions = getStyleOptions(featuresStyles);

    const clickedFeature: Feature = {
      id: featureData.id,
      geometry: featureData.geometry,
      type: featureData.type as FeatureType,
      properties: featureData.attributes || {},
      layer: featureData.layer,
      feature: existingFeature?.feature || null,
    };

    /*
     * 0ms is not a delay: it queues a macrotask so this runs after the current
     * turn. That lets the wait cursor paint and pending input process before
     * loadSingleFeature / importWKT blocks the main thread.
     */
    const timeoutId = window.setTimeout(() => {
      try {
        const loadedFeature = loadSingleFeature(
          clickedFeature,
          layer.id,
          drawingTools,
          styleOptions,
        );

        const updatedLoadedFeatures = loadedFeatures.filter(
          (feature) => feature.id !== loadedFeature.id,
        );

        if (!isNil(loadedFeature.feature)) {
          updatedLoadedFeatures.push(loadedFeature);

          if (mapLayer instanceof ImageryLayer) {
            const mvtProvider = mapLayer.imageryProvider;

            if (mvtProvider instanceof MVTImageryProvider) {
              mvtProvider.hideFeature(loadedFeature.id);
            }
          }
        }

        setLoadedFeatures(updatedLoadedFeatures);

        // Update layer with ALL loaded features to preserve edits.
        const updatedLayer: WorkspaceLayer = {
          ...layer,
          features: updatedLoadedFeatures,
          mapLayer,
        };

        updateLayer({
          [layer.id]: updatedLayer,
        });

        if (actionTools?.selectFeatureTool && !isNil(loadedFeature.feature)) {
          const geometryType = getGeometryType(loadedFeature.type);

          if (!isNil(geometryType)) {
            actionTools.selectFeatureTool.selectFeature({
              id: loadedFeature.id,
              type: geometryType,
            });
          }
        }
      } finally {
        setClickedFeatureId(null);
      }
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isSuccessClickedFeature, clickedFeatureResponse]);

  // useEffect - Handle clicked feature error.
  useEffect(() => {
    if (!isErrorClickedFeature) {
      return;
    }

    handleResponseErrorMessage(isErrorClickedFeature, clickedFeatureError);
    setClickedFeatureId(null);
  }, [isErrorClickedFeature, clickedFeatureError]);

  useEffect(() => {
    const selectFeatureTool = actionTools?.selectFeatureTool;

    if (!selectFeatureTool || !isSelectingFeature) {
      return;
    }

    selectFeatureTool.setOverrideCursorWait();

    return () => {
      selectFeatureTool.restoreOverrideCursor();
    };
  }, [actionTools, isSelectingFeature]);

  // useEffect - Setup click listener when entering edit mode.
  useEffect(() => {
    if (!isInEditMode || !cesiumProxy?.viewer) {
      // Clean up listener when exiting edit mode.
      if (removeClickListenerRef.current) {
        removeClickListenerRef.current();
        removeClickListenerRef.current = null;
      }

      // Exiting reverts the hiding, whether or not the edits were saved.
      if (layer.mapLayer && layer.mapLayer instanceof ImageryLayer) {
        showAllHiddenFeatures(layer.mapLayer);
      }

      setClickedFeatureId(null);
      setLoadedFeatures([]);
      return;
    }

    removeClickListenerRef.current = addCanvasClickListener(
      cesiumProxy.viewer.canvas,
      handleFeatureClick,
    );

    enableLayerEditing();

    return () => {
      if (removeClickListenerRef.current) {
        removeClickListenerRef.current();
        removeClickListenerRef.current = null;
      }
    };
  }, [isInEditMode, cesiumProxy]);

  // Renders.
  const layerMeatBallMenu = (
    <LayerMeatBallMenu
      layer={layer}
      showFeatureCount={showFeatureCount}
      setShowFeatureCount={setShowFeatureCount}
      setShowSelectAreaCategoryModal={setShowSelectAreaCategoryModal}
      showSublayers={showSublayers}
      setShowSublayers={setShowSublayers}
      isClampEnabled={isClampEnabled}
      handleClampToSurfaceChange={handleClampToSurfaceChange}
    />
  );

  const renderLayerMeatBallMenu = () => {
    return isCadLayer ? (
      <Tooltip
        hoverText="This CAD file contains sub-layers. You can manage their visibility from this kebab menu."
        placement={Placement.Right}
        show={layer.showCadLayerOnboarding}
      >
        <span onMouseEnter={clearCadLayerOnboarding}>{layerMeatBallMenu}</span>
      </Tooltip>
    ) : (
      layerMeatBallMenu
    );
  };

  return (
    <div className="workspace-active-layer-container">
      <DatasetDetailsTooltip
        layerName={layer.name}
        siteName={layer.site?.name}
        iterationName={layer.iteration?.name}
        layerType={layer.type}
        show={isCadLayer && layer.showCadLayerOnboarding ? false : undefined}
      >
        <ListItem
          className={className}
          disabled={isLoading || isErrorLayerResponse || isPointCloudIn2D}
          variant={getListVariant()}
          checked={isChecked}
          onClick={(event: React.MouseEvent<HTMLInputElement>) =>
            onClick(event, index)
          }
        >
          <ListItem.Avatar>
            <LayerIcon layer={layer} />
          </ListItem.Avatar>
          <ListItem.Content>
            <div className="workspace-active-layer__parent-details-container">
              <div className="workspace-active-layer__parent-details__name">
                {layer.name}
              </div>
              <div className="workspace-active-layer__parent-details__parent">
                <span>{layer.site?.name}</span>
                <Icon identifier={IconIdentifier.ChevronSmallRight} size={12} />
                <span>{layer.iteration?.name}</span>
              </div>
            </div>
          </ListItem.Content>
          <ListItem.Action>
            {showAreaCategoryPill && (
              <Tooltip
                hoverText={
                  areaCategoryOptions.filter((category) => {
                    return category.value === layer.areaCategory;
                  })[0]?.label
                }
                placement={Placement.Top}
                className="workspace-active-layer__area-category-tooltip"
              >
                <Pill
                  className="workspace-active-layer__area-category-pill"
                  shape={PillShape.Rectangle}
                  variant={PillVariant.Info}
                >
                  {areaCategoryPillLabel[layer.areaCategory]}
                </Pill>
              </Tooltip>
            )}
            {isInEditMode ? (
              <Pill shape={PillShape.Rectangle} variant={PillVariant.Warning}>
                Editing
              </Pill>
            ) : (
              <LayerActionItem
                layer={layer}
                refetchFeatures={refetchFeatures}
                onVisibilityToggle={
                  isCadLayer ? handleCadParentVisibilityToggle : undefined
                }
              />
            )}
            {isLoading && (
              <Spinner className="workspace-active-layer-spinner" />
            )}
            {renderLayerMeatBallMenu()}
          </ListItem.Action>
        </ListItem>
      </DatasetDetailsTooltip>

      {(layer.type === LayerType.Vector ||
        layer.type === LayerType.PointCloud) && (
        <FeatureCountTree
          layerType={layer.type}
          featuresCount={layer?.properties?.featuresCount}
          visible={showFeatureCount}
        />
      )}

      {isCadLayer && showSublayers && !isNil(layer.mapLayer) && (
        <DxfSublayerList
          nodes={cadNodes}
          hiddenCadNodeIds={hiddenCadNodeIds}
          onToggleNode={handleToggleNodeVisibility}
          cesiumProxy={cesiumProxy}
        />
      )}

      {/* Select Area Category Modal */}
      {showSelectAreaCategoryModal && (
        <SelectAreaCategoryModal
          layerAreaCategory={layer.areaCategory}
          onClose={onCloseSelectAreaCategory}
          onSave={onSaveSelectedAreaCategory}
          isLoading={isLoadingUpdateLayer}
        />
      )}
    </div>
  );
};
